import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import styles from '../styles/ConsentScreen.module.scss';
import { ReactComponent as PropdocsLogo } from '../../../shared/icons/propdocs-logo.svg';

interface ConsentScreenProps {
  document: {
    id: string;
    title: string;
  };
  signer: {
    email: string;
  };
  sender: {
    name: string;
    email: string;
  };
  onAccept: () => void;
}

export const ConsentScreen: React.FC<ConsentScreenProps> = ({ document, signer, sender, onAccept }) => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);

  const handleAccept = () => {
    if (consentChecked) {
      onAccept();
    }
  };

  const disclosureContent = (
    <div className={styles.disclosureTextContent}>
      <h4>Electronic Records and Signature Disclosure</h4>

      <p>By agreeing to use electronic signatures, you acknowledge that:</p>

      <p>Your electronic signature has the same legal effect as a handwritten signature.</p>

      <p>You consent to receive, review, and sign documents electronically.</p>

      <p>
        You may request paper copies or withdraw consent at any time by contacting the sender. Withdrawal will not
        affect documents already signed.
      </p>

      <p>To access and retain electronic records, you'll need:</p>

      <ul>
        <li>An internet-enabled device</li>
        <li>A modern web browser (Chrome, Firefox, Safari, or Edge)</li>
        <li>A valid email address</li>
        <li>PDF reader software</li>
      </ul>

      <p>
        For questions or support, contact <a href="mailto:support@propdocs.com">support@propdocs.com</a>
      </p>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.backgroundBlur}></div>
      <div className={styles.consentContainer}>
        <div className={styles.consentCard}>
          <div className={styles.header}>
            <div className={styles.logoContainer}>
              <PropdocsLogo className={styles.logo} />
            </div>
            <h1 className={styles.title}>Electronic Signature Consent</h1>
          </div>

          <div className={styles.documentInfo}>
            <div className={styles.infoRow}>
              <span className={styles.label}>Document: </span>
              <span className={styles.value}>{document.title}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Signer: </span>
              <span className={styles.value}>{signer.email}</span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.label}>Sender: </span>
              <span className={styles.value}>
                {sender.name} ({sender.email})
              </span>
            </div>
          </div>

          <div className={styles.consentSection}>
            <p className={styles.disclosureText}>
              Please read the{' '}
              <button className={styles.disclosureLink} onClick={() => setShowDisclosure(true)} type="button">
                Electronic Record and Signature Disclosure
              </button>
            </p>

            <div className={styles.consentCheckbox}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxText}>I agree to use electronic records and signatures</span>
              </label>
            </div>
          </div>

          <div className={styles.actions}>
            <Button variant="primary" onClick={handleAccept} disabled={!consentChecked} className={styles.continueBtn}>
              Continue to Sign
            </Button>
          </div>

          <div className={styles.footer}>
            <p className={styles.poweredBy}>Powered by Propdocs • Secured with Digital Certificates</p>
          </div>
        </div>
      </div>

      {/* Disclosure Modal/Tooltip */}
      {showDisclosure && (
        <div className={styles.modalOverlay} onClick={() => setShowDisclosure(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Electronic Record and Signature Disclosure</h3>
              <button className={styles.closeBtn} onClick={() => setShowDisclosure(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalContent}>{disclosureContent}</div>
            <div className={styles.modalFooter}>
              <Button onClick={() => setShowDisclosure(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
